/**
 * DG Basics — cards de produto (snippets/dg-product-card.liquid)
 * Delegação de eventos: funciona também para cards inseridos depois (recomendações, rolagem infinita).
 * - Troca de cor: atualiza foto, selo de desconto, preço, pagamento e links (com a variante escolhida).
 * - Variante única: "Comprar" adiciona ao carrinho com o evento do tema (sem JS, o formulário
 *   envia normalmente para o carrinho).
 */

function formatMoney(cents, format) {
  const fmt = (value, decimals, thousands, decimal) => {
    const [int, frac] = (value / 100).toFixed(decimals).split('.');
    const withThousands = int.replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
    return frac ? `${withThousands}${decimal}${frac}` : withThousands;
  };
  const template = format || 'R$ {{amount_with_comma_separator}}';
  return template.replace(/\{\{\s*(\w+)\s*\}\}/, (_, key) => {
    switch (key) {
      case 'amount_with_comma_separator':
        return fmt(cents, 2, '.', ',');
      case 'amount_no_decimals_with_comma_separator':
        return fmt(cents, 0, '.', ',');
      case 'amount_no_decimals':
        return fmt(cents, 0, ',', '.');
      case 'amount_with_period_and_space_separator':
        return fmt(cents, 2, ' ', '.');
      default:
        return fmt(cents, 2, ',', '.');
    }
  });
}

const dataOf = (card) => {
  if (!card.dgData) card.dgData = JSON.parse(card.querySelector('[data-dg-product]').textContent);
  return card.dgData;
};

function updateCard(card, variant, color) {
  const data = dataOf(card);
  const money = (cents) => formatMoney(cents, card.dataset.moneyFormat);

  const img = card.querySelector('.dg-card__img');
  if (img && variant.image) {
    img.removeAttribute('srcset');
    img.src = variant.image;
  }

  const name = card.querySelector('[data-color-name]');
  if (name) name.textContent = color;

  // Selo e preço: desconto só quando o preço comparativo é maior que o atual
  const onSale = variant.compare > variant.price;
  const badge = card.querySelector('[data-badge]');
  if (badge) {
    badge.hidden = !onSale;
    badge.textContent = onSale ? `${Math.round(((variant.compare - variant.price) * 100) / variant.compare)}% OFF` : '';
  }
  const price = card.querySelector('[data-price]');
  if (price) {
    const was = onSale
      ? `<s class="dg-card__was"><span class="visually-hidden">Preço original:</span> ${money(variant.compare)}</s>`
      : '';
    price.innerHTML = `${was}<strong class="dg-card__now"><span class="visually-hidden">Preço atual:</span> ${money(variant.price)}</strong>`;
  }

  // Pagamento (mesmas regras do Liquid)
  const inst = card.querySelector('[data-pay-inst]');
  if (inst) {
    let n = Number(card.dataset.installments) || 0;
    const min = Number(card.dataset.installmentMin) || 0;
    if (min > 0) n = Math.min(n, Math.floor(variant.price / min));
    inst.hidden = n < 2;
    if (n >= 2) inst.textContent = `ou ${n}x de ${money(Math.ceil(variant.price / n))} sem juros`;
  }
  const pix = card.querySelector('[data-pay-pix]');
  if (pix) {
    const off = Number(card.dataset.pix) || 0;
    pix.textContent = `${money(Math.floor((variant.price * (100 - off)) / 100))} no Pix`;
  }

  // Links (nome, foto, "Comprar") com a variante escolhida
  const url = `${data.url}?variant=${variant.id}`;
  card.querySelectorAll('[data-link]').forEach((link) => link.setAttribute('href', url));

  // Cor sem estoque: "Comprar" vira "Esgotado nesta cor"
  const buy = card.querySelector('[data-buy-link]');
  if (buy) {
    const colorAvailable = data.variants.some((v) => v.options[data.colorIndex] === color && v.available);
    const label = buy.querySelector('.dg-buy__label');
    if (colorAvailable) {
      buy.removeAttribute('aria-disabled');
      buy.removeAttribute('tabindex');
      if (label) label.textContent = 'Comprar';
    } else {
      buy.setAttribute('aria-disabled', 'true');
      buy.setAttribute('tabindex', '-1');
      if (label) label.textContent = 'Esgotado nesta cor';
    }
  }
}

document.addEventListener('change', (event) => {
  const input = event.target;
  if (!(input instanceof HTMLInputElement) || input.type !== 'radio') return;
  const card = input.closest('[data-dg-card]');
  if (!card) return;
  const data = dataOf(card);
  const color = input.value;
  const variant =
    data.variants.find((v) => v.options[data.colorIndex] === color && v.available) ||
    data.variants.find((v) => v.options[data.colorIndex] === color);
  if (variant) updateCard(card, variant, color);
});

// Link desativado (cor esgotada) não navega
document.addEventListener('click', (event) => {
  const link = event.target.closest?.('[data-buy-link][aria-disabled="true"]');
  if (link) event.preventDefault();
});

// Variante única: adiciona ao carrinho sem sair da página (mesmo evento do tema → gaveta + Yampi)
document.addEventListener('submit', async (event) => {
  const form = event.target;
  if (!(form instanceof HTMLFormElement) || !form.matches('[data-dg-quick-add]')) return;

  event.preventDefault(); // precisa ser síncrono (antes de qualquer await)
  let CartLinesUpdateEvent;
  try {
    ({ CartLinesUpdateEvent } = await import('@shopify/events'));
  } catch (e) {
    form.submit(); // sem o módulo do tema: envio tradicional para o carrinho
    return;
  }

  const button = form.querySelector('button[type="submit"]');
  const label = button?.querySelector('.dg-buy__label');
  const original = label?.textContent;
  if (button) button.disabled = true;
  if (label) label.textContent = 'Adicionando…';

  const routes = window.Theme?.routes || {};
  const body = new FormData(form);
  const sectionIds = [...document.querySelectorAll('cart-items-component')]
    .map((el) => el instanceof HTMLElement && el.dataset.sectionId)
    .filter(Boolean);
  if (sectionIds.length) body.append('sections', sectionIds.join(','));

  try {
    const response = await fetch(`${routes.cart_add_url || '/cart/add'}.js`, {
      method: 'POST',
      headers: { 'X-Requested-With': 'XMLHttpRequest', Accept: 'application/json' },
      body,
    });
    const result = await response.json();
    if (!response.ok || result.status) throw new Error(result.description || result.message || 'Não foi possível adicionar.');

    const itemsComponent = document.querySelector('cart-items-component');
    const cart =
      (itemsComponent && typeof itemsComponent.fetchCartData === 'function' && (await itemsComponent.fetchCartData())) ||
      (await (await fetch(`${routes.cart_url || '/cart'}.js`, { headers: { Accept: 'application/json' } })).json());

    const deferred = CartLinesUpdateEvent.createPromise();
    const quantity = Number(body.get('quantity')) || 1;
    form.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: [{ merchandiseId: String(body.get('id')), quantity }],
        promise: deferred.promise,
      })
    );
    deferred.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: {
        items: cart.items,
        source: 'dg-product-card',
        itemCount: quantity,
        productId: String(dataOf(form.closest('[data-dg-card]')).id),
        sections: result.sections,
        didError: false,
      },
    });
    if (label) label.textContent = 'Adicionado ✓';
    setTimeout(() => {
      if (label) label.textContent = original;
      if (button) button.disabled = false;
    }, 1600);
  } catch (error) {
    if (label) label.textContent = 'Tente novamente';
    if (button) button.disabled = false;
    console.error('[dg-product-card]', error);
    setTimeout(() => label && (label.textContent = original), 2200);
  }
});
